import { useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { Alert, Badge, Spinner } from '@/ui'
import styles from './AIPipeline.module.css'

interface ImageModel {
  id: string
  name: string
  provider: string
  providerType: 'openai' | 'gemini' | string
  providerModel: string
  costPerImage: number
  description: string
  facePreservation: string
  speed: string
  supportsReferenceImage: boolean
  supportsMultipleOutputs: boolean
  aspectRatio: string | null
  imageSize: string | null
  modelType: 'image'
  enabled: boolean
  isDefault: boolean
  isRecommended: boolean
  sortOrder: number
}

interface TextModel {
  id: string
  name: string
  provider: string
  providerType: string
  providerModel: string
  costPer1kInputTokens: number
  costPer1kOutputTokens: number
  maxContextTokens: number
  description: string
  modelType: 'text'
  enabled: boolean
  isDefault: boolean
  sortOrder: number
}

interface ModelsResponse {
  imageModels: ImageModel[]
  textModels: TextModel[]
}

interface ParamDoc {
  name: string
  source: string
  scope: string
  description: string
}

const IMAGE_PARAM_DOCS: ParamDoc[] = [
  {
    name: 'modelId',
    source: 'body.modelId || getDefaultModelId()',
    scope: '유저 요청 / DB',
    description:
      '사용할 이미지 모델 ID. AI Studio는 항상 빈값으로 호출하여 서버가 `is_default=true` 모델로 대체한다.',
  },
  {
    name: 'prompt',
    source: 'ai_prompt_blocks.prompt_text + ", variation {N}"',
    scope: 'DB + 런타임',
    description:
      '컨셉(프롬프트 블록)의 완성된 프롬프트 문자열. 뒤에 `, variation N` suffix를 붙여 동일 요청 다회 호출 시 결과를 다르게 유도.',
  },
  {
    name: 'providerModel',
    source: 'ai_registered_models.provider_model',
    scope: 'DB',
    description:
      '프로바이더 네이티브 모델 ID (예: `gemini-2.5-flash-image`, `gpt-image-1.5`, `dall-e-3`). 실제 API 호출의 model 필드로 그대로 전달.',
  },
  {
    name: 'imageUrls',
    source: 'body.referencePhotoIds → ai_reference_photos → S3 signed URL',
    scope: 'DB + S3',
    description:
      '유저가 선택한 참조사진의 signed URL 배열. 신랑/신부 2장이 기본. `supportsReferenceImage=false` 모델에서는 무시된다.',
  },
  {
    name: 'supportsReferenceImage',
    source: 'ai_registered_models.supports_reference_image',
    scope: 'DB',
    description:
      'true면 참조사진을 모델 호출에 포함. OpenAI는 images.edit 경로, Gemini는 inlineData parts로 전송.',
  },
  {
    name: 'imageSize',
    source: 'ai_registered_models.image_size',
    scope: 'DB',
    description:
      '결과 해상도. OpenAI: `1024x1024`/`1024x1536`/`1024x1792` 등. Gemini: `1K`/`2K`/`4K` (Nano Banana Pro만).',
  },
  {
    name: 'aspectRatio',
    source: 'ai_registered_models.aspect_ratio',
    scope: 'DB (Gemini 전용)',
    description:
      'Gemini 전용 종횡비 (`1:1`, `3:4`, `16:9` 등). OpenAI 경로에서는 무시.',
  },
  {
    name: 'variationIndex',
    source: '서버 생성 루프 index',
    scope: '런타임',
    description:
      '한 요청에서 여러 이미지 생성 시 0부터 증가. prompt 끝의 variation suffix 숫자와 providerJobId에 반영.',
  },
  {
    name: 'quality',
    source: '상수 `"standard"` (openai.ts)',
    scope: '코드 고정',
    description:
      'DALL-E 3 품질 옵션. 현재 `standard` 고정. `hd`로 올리려면 코드 수정 필요.',
  },
  {
    name: 'response_format',
    source: '상수 `"b64_json"`',
    scope: '코드 고정',
    description:
      'OpenAI 응답 포맷. base64로 받아 서버에서 S3로 업로드 후 URL 반환.',
  },
  {
    name: 'n',
    source: '상수 `1`',
    scope: '코드 고정',
    description:
      'OpenAI 호출당 이미지 개수. 항상 1이며, 다장 생성은 외부 루프로 처리.',
  },
  {
    name: 'responseModalities',
    source: "상수 `['image', 'text']`",
    scope: '코드 고정',
    description:
      'Gemini 멀티모달 응답 설정. 이미지 + 간단한 텍스트 부가 응답 수신.',
  },
]

function KeyValueTable({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className={styles.kv}>
      {items.map((i) => (
        <div key={i.label} className={styles.kvRow}>
          <dt className={styles.kvLabel}>{i.label}</dt>
          <dd className={styles.kvValue}>{i.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ImageModelCard({ model }: { model: ImageModel | null }) {
  if (!model) {
    return (
      <div className={styles.cardEmpty}>
        <p>
          기본 이미지 모델이 설정되어 있지 않습니다. <strong>AI 모델</strong> 페이지에서
          `is_default`를 활성화하세요.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>{model.name}</h3>
          <div className={styles.cardSubtitle}>
            <code>{model.id}</code>
          </div>
        </div>
        <div className={styles.badgeRow}>
          <Badge color="blue">{model.provider}</Badge>
          {model.isDefault && <Badge color="green">기본</Badge>}
          {!model.enabled && <Badge color="red">비활성</Badge>}
          {model.isRecommended && <Badge color="yellow">추천</Badge>}
        </div>
      </div>

      <KeyValueTable
        items={[
          { label: 'providerType', value: <code>{model.providerType}</code> },
          { label: 'providerModel', value: <code>{model.providerModel}</code> },
          { label: '장당 비용', value: `$${model.costPerImage.toFixed(4)}` },
          { label: '속도', value: model.speed },
          { label: '얼굴 보존', value: model.facePreservation },
          {
            label: '참조 이미지',
            value: model.supportsReferenceImage ? '지원' : '미지원',
          },
          { label: 'aspectRatio', value: model.aspectRatio ?? '—' },
          { label: 'imageSize', value: model.imageSize ?? '—' },
        ]}
      />
      {model.description && <p className={styles.cardDesc}>{model.description}</p>}
    </div>
  )
}

function TextModelCard({ model }: { model: TextModel | null }) {
  if (!model) return null
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>{model.name}</h3>
          <div className={styles.cardSubtitle}>
            <code>{model.id}</code>
          </div>
        </div>
        <div className={styles.badgeRow}>
          <Badge color="blue">{model.provider}</Badge>
          {model.isDefault && <Badge color="green">기본</Badge>}
          {!model.enabled && <Badge color="red">비활성</Badge>}
        </div>
      </div>
      <KeyValueTable
        items={[
          { label: 'providerType', value: <code>{model.providerType}</code> },
          { label: 'providerModel', value: <code>{model.providerModel}</code> },
          { label: 'Input $/1k', value: `$${model.costPer1kInputTokens.toFixed(4)}` },
          { label: 'Output $/1k', value: `$${model.costPer1kOutputTokens.toFixed(4)}` },
          { label: 'Max context', value: `${model.maxContextTokens.toLocaleString()} tok` },
        ]}
      />
      {model.description && <p className={styles.cardDesc}>{model.description}</p>}
    </div>
  )
}

export function AIPipeline() {
  const { data, isLoading, error } = useApiFetch<ModelsResponse>('/api/admin/ai-models')

  const defaultImageModel = useMemo(() => {
    if (!data) return null
    const enabled = data.imageModels.filter((m) => m.enabled)
    return enabled.find((m) => m.isDefault) ?? enabled[0] ?? null
  }, [data])

  const defaultTextModel = useMemo(() => {
    if (!data) return null
    const enabled = data.textModels.filter((m) => m.enabled)
    return enabled.find((m) => m.isDefault) ?? enabled[0] ?? null
  }, [data])

  const providerType = defaultImageModel?.providerType

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {error && <Alert variant="danger">{error}</Alert>}

      {/* 현재 기본 모델 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>현재 기본 모델</h2>
        <p className={styles.sectionHint}>
          유저가 AI Studio에서 사진을 생성할 때 실제로 호출되는 모델입니다.
          <code> ai_registered_models.is_default</code> 컬럼이 true인 행을 사용하며,
          없을 경우 <code>enabled=true</code> 중 <code>sort_order</code>가 가장 낮은 모델이
          대체로 선택됩니다.
        </p>

        <div className={styles.grid2}>
          <div>
            <h3 className={styles.subTitle}>이미지</h3>
            <ImageModelCard model={defaultImageModel} />
          </div>
          <div>
            <h3 className={styles.subTitle}>텍스트 (Liquid 변환·캡션 등)</h3>
            {defaultTextModel ? (
              <TextModelCard model={defaultTextModel} />
            ) : (
              <div className={styles.cardEmpty}>등록된 기본 텍스트 모델 없음</div>
            )}
          </div>
        </div>
      </section>

      {/* 파이프라인 흐름 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>요청 → 응답 파이프라인</h2>
        <ol className={styles.steps}>
          <li>
            <strong>유저</strong> — AI Studio에서 컨셉 선택 + 참조사진 선택. GenerateModal이{' '}
            <code>modelId: ''</code>로 전송.
          </li>
          <li>
            <strong>API 라우트</strong> — <code>apps/api/src/routes/ai.ts</code>에서
            <code> validateModelEnabled()</code>로 유효성 확인 후, 빈 modelId일 때{' '}
            <code>getDefaultModelId()</code> 호출.
          </li>
          <li>
            <strong>프롬프트 빌드</strong> — <code>aiPromptBlocks</code> 조회로 컨셉의{' '}
            <code>prompt_text</code> 추출. 뒤에 <code>, variation {'{'}N{'}'}</code> suffix 부착.
          </li>
          <li>
            <strong>참조사진 resolve</strong> — <code>referencePhotoIds</code> →{' '}
            <code>ai_reference_photos</code> → S3 signed URL 배열.
          </li>
          <li>
            <strong>프로바이더 라우팅</strong> —{' '}
            <code>apps/api/src/lib/ai/providers/index.ts</code>의 <code>getProvider()</code>가{' '}
            <code>providerType</code>으로 OpenAI / Gemini 구현체를 반환.
          </li>
          <li>
            <strong>API 호출</strong> — 프로바이더별 파라미터 스키마대로 외부 API 호출
            (아래 참고).
          </li>
          <li>
            <strong>결과 업로드</strong> — base64 결과를 S3에 PUT, signed URL 생성 후 SSE{' '}
            <code>image</code> 이벤트로 스트림.
          </li>
        </ol>
      </section>

      {/* OpenAI 호출 스키마 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          OpenAI 호출 파라미터
          {providerType === 'openai' && <Badge color="green">현재 사용</Badge>}
        </h2>
        <p className={styles.sectionHint}>
          소스: <code>apps/api/src/lib/ai/providers/openai.ts</code>
        </p>

        <div className={styles.codeBlock}>
          <div className={styles.codeHeader}>참조사진 없음 — DALL-E 3 경로</div>
          <pre>{`openai.images.generate({
  model: modelConfig.providerModel,
  prompt: \`\${block.promptText}, variation \${N+1}\`,
  size: modelConfig.imageSize ?? '1024x1792',
  quality: 'standard',
  response_format: 'b64_json',
  n: 1,
})`}</pre>
        </div>

        <div className={styles.codeBlock}>
          <div className={styles.codeHeader}>참조사진 있음 — gpt-image-1 / 1.5</div>
          <pre>{`openai.images.edit({
  model: modelConfig.providerModel,
  image: imageFiles,           // S3에서 다운받은 File[] (단수/배열)
  prompt: \`\${block.promptText}, variation \${N+1}\`,
  size: modelConfig.imageSize ?? '1024x1536',
})`}</pre>
        </div>
      </section>

      {/* Gemini 호출 스키마 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Gemini 호출 파라미터
          {providerType === 'gemini' && <Badge color="green">현재 사용</Badge>}
        </h2>
        <p className={styles.sectionHint}>
          소스: <code>apps/api/src/lib/ai/providers/gemini.ts</code> — 503/429 시 2s → 5s → 10s
          재시도. 기본 타임아웃 180초.
        </p>

        <div className={styles.codeBlock}>
          <div className={styles.codeHeader}>generateContent (이미지 입출력)</div>
          <pre>{`ai.models.generateContent({
  model: modelConfig.providerModel,
  contents: [{
    role: 'user',
    parts: [
      // 참조사진 (supportsReferenceImage=true일 때만)
      ...imageUrls.map(url => ({
        inlineData: { mimeType, data: base64(url) },
      })),
      { text: \`\${block.promptText}, variation \${N+1}\` },
    ],
  }],
  config: {
    responseModalities: ['image', 'text'],
    imageConfig: {
      // 모델 DB에 설정된 경우만 포함
      aspectRatio: modelConfig.aspectRatio,  // 예: '3:4'
      imageSize: modelConfig.imageSize,      // 예: '2K'
    },
  },
})`}</pre>
        </div>
      </section>

      {/* 파라미터 설명 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>파라미터 사전</h2>
        <p className={styles.sectionHint}>
          각 파라미터의 값 출처와 의미. DB는 <code>ai_registered_models</code> /{' '}
          <code>ai_prompt_blocks</code> / <code>ai_reference_photos</code> 참조.
        </p>
        <table className={styles.paramTable}>
          <thead>
            <tr>
              <th>파라미터</th>
              <th>출처</th>
              <th>범주</th>
              <th>설명</th>
            </tr>
          </thead>
          <tbody>
            {IMAGE_PARAM_DOCS.map((p) => (
              <tr key={p.name}>
                <td>
                  <code>{p.name}</code>
                </td>
                <td>
                  <code className={styles.srcCell}>{p.source}</code>
                </td>
                <td>
                  <span className={styles.scopeTag}>{p.scope}</span>
                </td>
                <td>{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
